import { makeObservable, observable, computed, action, reaction } from 'mobx';
import type { FirestoreUserSettings } from '@creative-kit/shared';
import type ft from 'firebase';
import type { RootStore } from '../logic';
import { db } from '../logic/data';

export class UiProjectNotesStore {
  notesContent: Record<string, string> = {};
  private stopListeningToNotes = () => {};
  private reactionDisposer: (() => void) | null = null;

  constructor(private rootStore: RootStore) {
    makeObservable(this, {
      notesContent: observable,
      watchNotes: action.bound,
      setProjectNotes: action,
      currentProjectNotes: computed,
    });

    // Watch notes when session or user changes
    this.reactionDisposer = reaction(
      () => ({
        sessionId: this.rootStore.sessionsStore.currentSession?.id,
        userId: this.rootStore.authStore.user?.uid,
      }),
      ({ sessionId, userId }) => {
        this.watchNotes(sessionId, userId);
      },
      { fireImmediately: true },
    );
  }

  dispose() {
    if (this.reactionDisposer) {
      this.reactionDisposer();
      this.reactionDisposer = null;
    }
    this.stopListeningToNotes();
    this.resetNotes();
  }

  watchNotes = (sessionId: string | undefined, userId: string | undefined) => {
    this.stopListeningToNotes();
    this.resetNotes();

    if (sessionId && userId) {
      const key = `${sessionId}-${userId}`;
      const ref = db.sessionUserSettings(sessionId, userId);
      this.stopListeningToNotes = ref.onSnapshot(
        (snapshot: ft.firestore.DocumentSnapshot<FirestoreUserSettings>) => {
          this.updateNotes(key, snapshot);
        },
      );
    }
  };

  resetNotes = action(() => {
    this.notesContent = {};
  });

  updateNotes = action(
    (key: string, snapshot: ft.firestore.DocumentSnapshot<FirestoreUserSettings>) => {
      const data = snapshot.data();
      if (data?.privateNotes !== undefined) {
        this.notesContent[key] = data.privateNotes;
      } else {
        this.notesContent[key] = '';
      }
    },
  );

  get currentProjectNotes(): string {
    const session = this.rootStore.sessionsStore.currentSession;
    const userId = this.rootStore.authStore.user?.uid;
    if (session && userId) {
      const key = `${session.id}-${userId}`;
      return this.notesContent[key] || '';
    }
    return '';
  }

  async setProjectNotes(content: string): Promise<void> {
    const session = this.rootStore.sessionsStore.currentSession;
    const userId = this.rootStore.authStore.user?.uid;
    if (session && userId) {
      const key = `${session.id}-${userId}`;
      this.notesContent[key] = content;
      try {
        const userSettingsRef = db.sessionUserSettings(session.id, userId);
        const currentDoc = await userSettingsRef.get();
        const currentData = currentDoc.data() || {};
        await userSettingsRef.set(
          {
            ...currentData,
            privateNotes: content,
          },
          { merge: true },
        );
      } catch (error) {
        console.error('Error saving private notes:', error);
      }
    }
  }
}
